import React, { ChangeEvent, Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { applyGroupFilter } from "@formant/core/common/combineFilters";
import { defined } from "@formant/core/common/defined";
import { narrowError } from "@formant/core/common/narrowError";
import { ResponseError } from "@formant/core/common/ResponseError";
import { TimeWindow } from "@formant/core/common/TimeWindow";
import { duration } from "@formant/core/model/duration";
import { IChannel } from "@formant/core/model/IChannel";
import { IDevice } from "@formant/core/model/IDevice";
import { IFilter } from "@formant/core/model/IFilter";
import { IGroup } from "@formant/core/model/IGroup";
import { Interval } from "@formant/core/model/Interval";
import { IOrganization } from "@formant/core/model/IOrganization";
import { IUser } from "@formant/core/model/IUser";
import { IValidation } from "@formant/core/model/IValidation";
import { Span } from "@formant/core/model/Span";
import { InputElement } from "@formant/ui/component/common/Field";

import { track } from "../../../analytics";
import { observeStore, viewStore } from "../../../stores";
import { createDeviceViewShareUrl } from "../../../utils/createDeviceViewShareUrl";
import { createTeleopShareUrl } from "../../../utils/createTeleopShareUrl";
import { formatDateTime } from "../../../utils/formatDateTime";
import { ButtonsBar } from "../../common/ButtonsBar";
import { Form } from "../../common/Form";
import { Heading3 } from "../../common/Heading3";
import { Popup } from "../../common/Popup";
import { SelectField } from "../../common/SelectField";
import { StandardButton } from "../../common/StandardButton";
import { Switch } from "../../common/Switch";
import { TextareaField } from "../../common/TextareaField";
import { ValidationMessages } from "../../common/ValidationMessages";
import * as styles from "./CreateShare.scss";
import { Expiration } from "./expiration";

export interface ICreateShareProps {
    visible: boolean;
    channel?: IChannel;
    device?: IDevice;
    group?: IGroup;
    user: IUser;
    organization: IOrganization;
    isTimelineVisible: boolean;
    onClose: () => void;
    onLinkCreated: (url: string, teleopUrl?: string) => void;
}

@observer
export class CreateShare extends Component<ICreateShareProps> {
    private time: Date = observeStore.time;
    @observable
    private numIntervals: number = 1;
    @observable
    private interval: Interval = "hour";
    @observable
    private timeRange: string = "hour";
    @observable
    private span: Span = "around";
    @observable
    private expiration: Expiration = "hour";
    @observable
    private note: string = "";
    @observable
    private validation: IValidation = {};
    @observable
    private loading: boolean = false;
    @observable
    private allowTeleop: boolean = false;

    public componentDidMount() {
        const { channel, device, group, isTimelineVisible } = this.props;
        if (!isTimelineVisible) {
            this.expiration = "never";
            this.interval = "year";
            this.numIntervals = 1000;
        }
        this.note = `See ${
            channel?.name || device?.name || group?.name || ""
        } in action`;
    }

    public render() {
        const { visible, onClose, group, device, isTimelineVisible } =
            this.props;
        const { time } = this;

        let shareName = "Channel";
        if (group) {
            shareName = "Group";
        } else if (device) {
            shareName = "Device";
        }

        return (
            <Popup visible={visible} className={styles["create-share"]}>
                <Heading3 className={styles.heading}>Share</Heading3>
                <Form
                    className={styles.content}
                    onSubmit={this.onSubmit}
                    onLoading={this.onLoading}
                >
                    <p>
                        Choose how you'd like to share this{" "}
                        {shareName.toLowerCase()}'s data outside of your
                        organization.{" "}
                        {isTimelineVisible &&
                            "The shared time window is based on the timeline's current position."}
                    </p>
                    {isTimelineVisible && (
                        <p className={styles.options}>
                            <SelectField
                                label=""
                                small={true}
                                value={this.timeRange}
                                className={styles["inline-field"]}
                                onChange={this.onChangeInterval}
                            >
                                <option value="minute">1 minute</option>
                                <option value="5minutes">5 minutes</option>
                                <option value="10minutes">10 minutes</option>
                                <option value="15minutes">15 minutes</option>
                                <option value="30minutes">30 minutes</option>
                                <option value="hour">1 hour</option>
                                <option value="day">1 day</option>
                            </SelectField>{" "}
                            of data{" "}
                            <SelectField
                                label=""
                                small={true}
                                value={this.span}
                                className={styles["inline-field"]}
                                onChange={this.onChangeSpan}
                            >
                                <option value="around">around</option>
                                <option value="before">before</option>
                                <option value="after">after</option>
                            </SelectField>{" "}
                            <span className={styles.time}>
                                {formatDateTime(time, { seconds: false })}
                            </span>
                            .
                        </p>
                    )}
                    <Switch
                        className={styles.teleop}
                        state={this.allowTeleop}
                        onChange={this.onToggleAllowTeleop}
                        label="Allow teleoperation"
                        stateLabels={["Deny", "Allow"]}
                        info="Enable this to allow the share to use teleoperation capabilities of the current user"
                    />
                    <p>Message</p>
                    <TextareaField
                        label=""
                        value={this.note}
                        className={styles.note}
                        invalid={!!this.validation.note}
                        onChange={this.onChangeNote}
                    />
                    <ValidationMessages validation={this.validation} />
                    <div className={styles.bottom}>
                        <p>
                            Link expires after{" "}
                            <SelectField
                                label=""
                                small={true}
                                value={this.expiration}
                                className={styles["inline-field"]}
                                onChange={this.onChangeExpiration}
                            >
                                <option value="hour">1 hour</option>
                                <option value="day">1 day</option>
                                <option value="week">1 week</option>
                                <option value="never">Never</option>
                            </SelectField>
                        </p>
                        <ButtonsBar minimal={true} className={styles.submit}>
                            <StandardButton
                                id="close-button"
                                type="button"
                                onClick={onClose}
                                dark={true}
                            >
                                Cancel
                            </StandardButton>
                            <StandardButton
                                primary={true}
                                loading={this.loading}
                                data-testid="create-share-link-button"
                            >
                                Create Share Link
                            </StandardButton>
                        </ButtonsBar>
                    </div>
                </Form>
            </Popup>
        );
    }

    private onToggleAllowTeleop = () => {
        this.allowTeleop = !this.allowTeleop;
    };

    private onChangeSpan = (event: ChangeEvent<InputElement>) => {
        this.span = event.currentTarget.value as Span;
    };

    private onChangeInterval = (event: ChangeEvent<InputElement>) => {
        const { value } = event.currentTarget;
        this.timeRange = value;
        switch (value) {
            case "5minutes":
                this.numIntervals = 5;
                this.interval = "minute";
                break;
            case "10minutes":
                this.numIntervals = 10;
                this.interval = "minute";
                break;
            case "15minutes":
                this.numIntervals = 15;
                this.interval = "minute";
                break;
            case "30minutes":
                this.numIntervals = 30;
                this.interval = "minute";
                break;
            default:
                this.numIntervals = 1;
                this.interval = value as Interval;
                break;
        }
    };

    private onChangeExpiration = (event: ChangeEvent<InputElement>) => {
        this.expiration = event.currentTarget.value as Expiration;
    };

    private onChangeNote = (event: ChangeEvent<InputElement>) => {
        this.note = event.currentTarget.value;
    };

    private get expirationDuration() {
        switch (this.expiration) {
            case "hour":
                return duration.hour;
            case "day":
                return duration.day;
            case "week":
                return duration.week;
            case "never":
                return undefined;
        }
    }

    private get expirationTime() {
        const { expirationDuration } = this;
        if (expirationDuration === undefined) {
            return undefined;
        }
        return new Date(Date.now() + expirationDuration);
    }

    private onSubmit = async () => {
        const { channel, device, group, user, onLinkCreated } = this.props;

        const {
            expirationTime,
            note,
            time,
            numIntervals,
            interval,
            span,
            allowTeleop,
        } = this;

        const timeWindow = new TimeWindow(time, numIntervals, interval, span);

        this.validation = {};

        const userName = user.firstName;

        const channelId = channel ? defined(channel.id) : undefined;

        let filter: IFilter = {
            ...channel?.filter,
        };

        if (device) {
            filter.deviceIds = [defined(device.id)];
        }

        if (group) {
            filter = applyGroupFilter(group, filter);
        }

        try {
            const share = {
                channelId,
                scope: {
                    ...filter,
                    start: timeWindow.start.toISOString(),
                    end: timeWindow.end.toISOString(),
                },
                time: this.time.toISOString(),
                expiration: expirationTime
                    ? expirationTime.toISOString()
                    : undefined,
                message: note,
                delegateTeleop: this.allowTeleop,
                userName,
            };
            const deviceViewUrl = await createDeviceViewShareUrl(share);

            let teleopUrl: string | undefined;
            if (allowTeleop) {
                const teleopShare = {
                    ...share,
                    delegateTeleop: this.allowTeleop,
                };
                teleopUrl = await createTeleopShareUrl(
                    defined(device?.id),
                    teleopShare,
                );
            }

            onLinkCreated(deviceViewUrl, teleopUrl);
            void this.trackAnalyticsShare();
            track("Share Created");
        } catch (error) {
            const responseError = narrowError(
                error,
                ResponseError,
                _ => _.statusCode === 422,
            );
            this.validation = responseError.validationErrors;
        }
    };

    private trackAnalyticsShare = async () => {
        const currentView = await viewStore.getCurrentView();
        if (!!currentView && currentView.layoutType === "analytics") {
            track("Analytics page shared");
        }
    };

    private onLoading = (loading: boolean) => {
        this.loading = loading;
    };
}
